// This is a JavaScript code that defines a React component to manage the state of the shopping cart. The code creates a context and exports it as StateContext. The context provides state values and functions that can be accessed by its child components.

// The state variables managed by this component include products, collections, categories, banners, cartItems, totalPrice, totalQuantities, and taxAndVat. There are also helper functions such as calculateTax, increaseQty, decreaseQty, onAddToCart, and onRemove.

// The useEffect hooks are used to trigger certain actions when the state changes. For example, useEffect(() => {}, [taxAndVat]) will run when taxAndVat changes.

// The code also uses the localStorage API to persist data such as the shopping cart items and the total price. When the component is mounted, it checks if there are any items in the cart and initializes the state accordingly.

// Finally, the code uses the toast library to display notifications when certain actions are performed.

import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-hot-toast";

const Context = createContext();

// Helper function to safely access localStorage
const getStoredCartItems = () => {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      return JSON.parse(localStorage.getItem("cartItems")) ?? [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

// Helper function to safely set cart items in localStorage
const setStoredCartItems = (items) => {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      localStorage.setItem("cartItems", JSON.stringify(items));
    } catch (e) {
      console.error("Error saving to localStorage:", e);
    }
  }
};

export const StateContext = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [deliveryPrices, setDeliveryPrices] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [taxAndVat, setTaxAndVat] = useState(0);
  const [totalQuantities, setTotalQuantities] = useState(0);
  const [qty, setQty] = useState(1);
  let foundProduct;
  let index;

  const calculateTax = (totalPrice) => {
    const feeCap = 2000,
      decimalFee = 0.015,
      flatFee = 100;
    let finalPrice;
    // To calculate tax Calculate applicable fees: Based on your fee structure, you calculate the fees the customer would be charged if you were not passing these fees to them. This is because of the transaction fee cap.
    const applicableFee = +decimalFee * +totalPrice + +flatFee;
    console.log(applicableFee);
    if (applicableFee > feeCap) {
      finalPrice = totalPrice + feeCap;
      return +finalPrice;
    } else {
      // Final Amount = ({Price} / (1 - {Decimal Fee})) + 0.01
      finalPrice = (totalPrice + flatFee) / (1 - decimalFee);
      finalPrice = finalPrice + 0.01;
      return +finalPrice;
    }
  };

  // Initialize cart from localStorage on mount
  useEffect(() => {
    const storedCartItems = getStoredCartItems();
    setCartItems(storedCartItems);

    let totalPriceDeterminer =
      storedCartItems.length === 0
        ? 0
        : storedCartItems.length >= 1 &&
          storedCartItems
            ?.map((item) => item.productPrice * item.quantity)
            .reduce((accumulator, currentValue) => accumulator + currentValue);
    setTotalPrice(totalPriceDeterminer);

    let totalQuantitiesDeterminer =
      storedCartItems.length === 0
        ? 0
        : storedCartItems.length >= 1 &&
          storedCartItems
            ?.map((item) => item.quantity)
            ?.reduce((accumulator, currentValue) => accumulator + currentValue);
    setTotalQuantities(totalQuantitiesDeterminer);

    setTaxAndVat(
      Math.round(calculateTax(totalPriceDeterminer) - totalPriceDeterminer)
    );
  }, []);

  useEffect(() => {
    setTaxAndVat(Math.round(calculateTax(totalPrice) - totalPrice));
  }, [totalPrice]);
  // Increase Quantity
  const increaseQty = () => {
    setQty((prevQty) => prevQty + 1);
  };
  // Decrease Quantity
  const decreaseQty = () => {
    setQty((prevQty) => {
      if (prevQty - 1 < 1) return 1;
      return prevQty - 1;
    });
  };
  // Adding to Cart
  const onAddToCart = (
    product,
    quantity,
    color,
    colorName,
    size,
    len,
    type
  ) => {
    const CheckProductInCart = cartItems.find(
      (item) => item.productName === product.productName
    );

    if (CheckProductInCart) {
      const updatedCartItems = cartItems.map((cartItem) => {
        if (cartItem.id === product.id) {
          return {
            ...cartItem,
            quantity: cartItem.quantity + quantity,
            color,
            colorName,
            size,
            len: len ? len : cartItem.len,
            type: type ? type : cartItem.type,
          };
        }
        return cartItem;
      });
      setStoredCartItems(updatedCartItems);
      setCartItems(updatedCartItems);
      setTotalPrice(
        (prevTotalPrice) => prevTotalPrice + product.productPrice * quantity
      );
      setTaxAndVat(Math.round(calculateTax(totalPrice) - totalPrice));

      setTotalQuantities(
        (prevTotalQuantities) => prevTotalQuantities + quantity
      );
    } else {
      if (quantity && color && colorName && size) {
        const newCartItem = {
          ...product,
          quantity,
          color,
          colorName,
          size,
          len: len ? len : "",
          type: type ? type : "",
        };
        const newCart = [...cartItems, newCartItem];
        setStoredCartItems(newCart);
        setCartItems(newCart);
        setTotalPrice(
          (prevTotalPrice) => prevTotalPrice + product.productPrice * quantity
        );
        setTaxAndVat(Math.round(calculateTax(totalPrice) - totalPrice));

        setTotalQuantities(
          (prevTotalQuantities) => prevTotalQuantities + quantity
        );
        toast.success(`${quantity} ${product.productName} added successfully`);
      } else {
        toast.error("Can't add item to cart");
      }
    }
  };
  // Item Removal Function
  const onRemove = (productName) => {
    const updatedCart = cartItems.filter(
      (item) => item.productName !== productName
    );
    setStoredCartItems(updatedCart);
    setCartItems(updatedCart);
    setTotalQuantities(
      updatedCart.length === 0
        ? 0
        : updatedCart.length >= 1 &&
            updatedCart
              ?.map((item) => item.quantity)
              ?.reduce(
                (accumulator, currentValue) => accumulator + currentValue
              )
    );
    setTotalPrice(
      updatedCart.length === 0
        ? 0
        : updatedCart.length >= 1 &&
            updatedCart
              ?.map((item) => item.productPrice * item.quantity)
              .reduce((accumulator, currentValue) => accumulator + currentValue)
    );
    setTaxAndVat(Math.round(calculateTax(totalPrice) - totalPrice));
  };
  // Changing items quantity in cart
  const toggleCartItemQuantity = (productName, value) => {
    foundProduct = cartItems.find(
      (cartItem) => cartItem.productName === productName
    );
    index = cartItems.findIndex(
      (cartItem) => cartItem.productName === productName
    );
    let newCartItems = cartItems.filter(
      (cartItem) => cartItem.productName !== productName
    );
    if (value === "increment") {
      newCartItems.splice(index, 0, {
        ...foundProduct,
        quantity: foundProduct.quantity + 1,
      });
      setStoredCartItems(newCartItems);
      setCartItems([...newCartItems]);
      setTotalPrice(
        newCartItems.length === 0
          ? 0
          : newCartItems.length >= 1 &&
              newCartItems
                ?.map((item) => item.productPrice * item.quantity)
                .reduce(
                  (accumulator, currentValue) => accumulator + currentValue
                )
      );
      setTaxAndVat(Math.round(calculateTax(totalPrice) - totalPrice));
      setTotalQuantities(
        newCartItems.length === 0
          ? 0
          : newCartItems.length >= 1 &&
              newCartItems
                ?.map((item) => item.quantity)
                ?.reduce(
                  (accumulator, currentValue) => accumulator + currentValue
                )
      );
    } else if (value === "decrement") {
      if (foundProduct.quantity > 1) {
        newCartItems.splice(index, 0, {
          ...foundProduct,
          quantity: foundProduct.quantity - 1,
        });
        setStoredCartItems(newCartItems);
        setCartItems([...newCartItems]);
        setTotalPrice(
          newCartItems.length === 0
            ? 0
            : newCartItems.length > 0 &&
                newCartItems
                  ?.map((item) => item.productPrice * item.quantity)
                  .reduce(
                    (accumulator, currentValue) => accumulator + currentValue
                  )
        );
        setTaxAndVat(Math.round(calculateTax(totalPrice) - totalPrice));
        setTotalQuantities(
          newCartItems.length === 0
            ? 0
            : newCartItems.length >= 1 &&
                newCartItems
                  ?.map((item) => item.quantity)
                  ?.reduce(
                    (accumulator, currentValue) => accumulator + currentValue
                  )
        );
      } else {
        onRemove(productName);
      }
    }
  };

  return (
    <Context.Provider
      value={{
        cartItems,
        totalPrice,
        totalQuantities,
        taxAndVat,
        qty,
        increaseQty,
        decreaseQty,
        onAddToCart,
        setTotalPrice,
        toggleCartItemQuantity,
        setCartItems,
        setTotalQuantities,
        setTaxAndVat,
        products,
        setProducts,
        collections,
        setCollections,
        categories,
        setCategories,
        banners,
        setBanners,
        deliveryPrices,
        setDeliveryPrices,
      }}
    >
      {children}
    </Context.Provider>
  );
};
export const useStateContext = () => useContext(Context);
